import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArtContext } from "../Contexts/ArtContext";
import { ArtistContext } from "../Contexts/ArtistContext";
import { CategoryContext } from "../Contexts/CategoryContext";
import { CollectionContext } from "../Contexts/CollectionContext";
import Alert from "../components/Alert";

const ArtEditForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getArt, updateArt } = useContext(ArtContext);
  const { artists } = useContext(ArtistContext);
  const { category } = useContext(CategoryContext);
  const { allCollections } = useContext(CollectionContext);
  

  const [artData, setArtData] = useState({
    artName: "",
    productCode: "",
    artDescription: "",
    artSize: "",
    artMaterial: "",
    quantity: "",
    artists: "",
    collections: "",
    categories: "",
  });

  const [editions, setEditions] = useState([
    { editionType: "Original Art Work", price: "" },
  ]);
  const [artImageCover, setArtImageCover] = useState(null);
  const [artImages, setArtImages] = useState([]);
  const [artImageCoverPreview, setArtImageCoverPreview] = useState(null);
  const [artImagesPreview, setArtImagesPreview] = useState([]);
  const [ alert, setAlert ] = useState({status: "", message: ""})

  useEffect(() => {
    const loadArtDetails = async () => {
      try {
        const art = await getArt(id);
        setArtData({
          artName: art.artName,
          productCode: art.productCode,
          artDescription: art.artDescription,
          artSize: art.artSize,
          artMaterial: art.artMaterial,
          quantity: art.quantity,
          artists: art.artists || "",
          collections: art.collections || "",
          categories: art.categories || "",
        });
        setEditions(art.editions || []);
        setArtImageCoverPreview(
          art.artImageCover ? `${art.artImageCover}` : ""
        );
        setArtImagesPreview(
          art.artImages.map((img) => `${img}`)
        );
      } catch (error) {
        console.error("Failed to load art details", error);
      }
    };

    loadArtDetails();
  }, [id]);

  const handleCancel = () => {
    navigate("/admin/view/art");
  };

  // Handle input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // If "None" is selected, set the value to either null or an empty string
    const formattedValue = value === "null" ? "" : value;
    // console.log(`Name: ${name}, Value: ${formattedValue}`);

    setArtData({ ...artData, [name]: formattedValue });
  };

  // Handle cover image change
  const handleImageCoverChange = (event) => {
    const file = event.target.files[0];
    setArtImageCover(file);
    setArtImageCoverPreview(URL.createObjectURL(file));
  };

  // Handle multiple images change
  const handleArtImagesChange = (event) => {
    const files = Array.from(event.target.files);
    setArtImages(files);
    setArtImagesPreview(files.map((file) => URL.createObjectURL(file)));
  };

  // Handle editions
  const handleAddEdition = () => {
    setEditions([...editions, { editionType: "Original Art Work", price: "" }]);
  };

  const handleRemoveEdition = (index) => {
    const newEditions = editions.filter((_, i) => i !== index);
    setEditions(newEditions);
  };

  const handleEditionChange = (index, event) => {
    const { name, value } = event.target;
    const newEditions = editions.map((edition, i) =>
      i === index ? { ...edition, [name]: value } : edition
    );
    setEditions(newEditions);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Remove _id from editions
    const editionsWithoutId = editions.map(({ _id, ...rest }) => rest);

    // Create FormData to send files
    const formData = new FormData();
    formData.append("artName", artData.artName);
    formData.append("productCode", artData.productCode);
    formData.append("artDescription", artData.artDescription);
    formData.append("artSize", artData.artSize);
    formData.append("artMaterial", artData.artMaterial);
    formData.append("quantity", artData.quantity);
    formData.append("artists", artData.artists);
    // Only append category if no collection is selected

    if (artData.collections && artData.collections.length > 0) {
      formData.append("collections", artData.collections);
    } else if (artData.categories && artData.categories.length > 0) {
      formData.append("categories", artData.categories);
    }
    formData.append("editions", JSON.stringify(editionsWithoutId));

    if (artImageCover) {
      formData.append("artImageCover", artImageCover);
    }

    artImages.forEach((image) => {
      formData.append(`artImages`, image);
    });

    // for (let pair of formData.entries()) {
    //   console.log(`${pair[0]}: ${pair[1]}`);
    // }

    try {
      await updateArt(id, formData); // Update the art
      // console.log("Art updated successfully");
      setAlert({ status: "success", message: "Art updated successfully!" });
      setTimeout(() => {
        navigate("/admin/view/art");
      }, 2000);
    } catch (error) {
      console.log(error);
      const errorMessage = error.message || "Failed to update art.";
      setAlert({ status: "error", message: errorMessage });
    }
  };

  return (
    <div className="bg-gray-100 py-10">
      {alert.message && (
            <Alert
              status={alert.status}
              message={alert.message}
              onClose={() => setAlert({ status: "", message: "" })}
            />
          )}
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">
          Update Art Information
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="artName"
              className="block text-gray-700 font-medium mb-2"
            >
              Art Name
            </label>
            <input
              type="text"
              id="artName"
              name="artName"
              value={artData.artName}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="productCode"
              className="block text-gray-700 font-medium mb-2"
            >
              Product Code
            </label>
            <input
              type="text"
              id="productCode"
              name="productCode"
              value={artData.productCode}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="artDescription"
              className="block text-gray-700 font-medium mb-2"
            >
              Art Description
            </label>
            <textarea
              id="artDescription"
              name="artDescription"
              value={artData.artDescription}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              rows="4"
            ></textarea>
          </div>

          <div className="mb-4">
            <label
              htmlFor="artImageCover"
              className="block text-gray-700 font-medium mb-2"
            >
              Art Image Cover
            </label>
            {artImageCoverPreview && (
              <img
                src={artImageCoverPreview}
                alt="Art Cover Preview"
                className="mb-2 w-32 h-32 object-cover"
              />
            )}
            <input
              type="file"
              id="artImageCover"
              name="artImageCover"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              onChange={handleImageCoverChange}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="artImages"
              className="block text-gray-700 font-medium mb-2"
            >
              Art Images (multiple files)
            </label>
            {artImagesPreview.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`Art Preview ${index}`}
                className="mb-2 w-32 h-32 object-cover"
              />
            ))}
            <input
              type="file"
              id="artImages"
              name="artImages"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              multiple
              onChange={handleArtImagesChange}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="artSize"
              className="block text-gray-700 font-medium mb-2"
            >
              Art Size
            </label>
            <input
              type="text"
              id="artSize"
              name="artSize"
              value={artData.artSize}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="artMaterial"
              className="block text-gray-700 font-medium mb-2"
            >
              Art Material
            </label>
            <input
              type="text"
              id="artMaterial"
              name="artMaterial"
              value={artData.artMaterial}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editions"
              className="block text-gray-700 font-medium mb-2"
            >
              Editions
            </label>
            {editions.map((edition, index) => (
              <div key={index} className="mb-2 flex space-x-2">
                <input
                  type="text"
                  name="editionType"
                  value={edition.editionType}
                  onChange={(e) => handleEditionChange(index, e)}
                  className="w-1/2 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
                <input
                  type="number"
                  name="price"
                  value={edition.price}
                  onChange={(e) => handleEditionChange(index, e)}
                  className="w-1/2 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveEdition(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddEdition}
              className="text-blue-600 hover:text-blue-800"
            >
              Add Edition
            </button>
          </div>

          <div className="mb-4">
            <label
              htmlFor="quantity"
              className="block text-gray-700 font-medium mb-2"
            >
              Quantity
            </label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={artData.quantity}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="artists"
              className="block text-gray-700 font-medium mb-2"
            >
              Artist
            </label>
            <select
              id="artists"
              name="artists"
              value={artData.artists}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              {artists.map((artist) => (
                <option key={artist._id} value={artist._id}>
                  {artist.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label
              htmlFor="categories"
              className="block text-gray-700 font-medium mb-2"
            >
              Category
            </label>
            <select
              id="categories"
              name="categories"
              value={artData.categories || ""}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value='""'>None</option>
              {category.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label
              htmlFor="collections"
              className="block text-gray-700 font-medium mb-2"
            >
              Collection
            </label>
            <select
              id="collections"
              name="collections"
              value={artData.collections || ""}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="">None</option>
              {allCollections.map((collection) => (
                <option key={collection._id} value={collection._id}>
                  {collection.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-500 text-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArtEditForm;
